<template>
  <div class="container my-4">
    <TopNav>
      <router-link
        v-if="isAdvancedDisplay"
        :to="{
          name: 'studio-dashboard',
          params: {
            studio_url: teacherStudioUrl,
          },
        }"
        class="undecorated-router-link"
        target="_blank"
      >
        <NavItem
          icon="far fa-eye"
          :text="$t('LABEL_EDITOR.LABEL.VIEW_ON_SITE')"
        />
      </router-link>
      <NavItem
        icon="far fa-repeat"
        :text="$t('LABEL_EDITOR.LABEL.RESET_ALL_TO_DEFAULT')"
        @click.native="showDeleteTranslationsModal"
      />
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        @click.native="saveTranslations"
        :pulse="isDirty"
        :loading="inProgress"
      />
    </TopNav>
    <div class="my-4">
      <h1>{{ $t('LABEL_EDITOR.LABEL.CUSTOM_LABELS') }}</h1>
      <div class="mt-2">
        {{ $t('LABEL_EDITOR.TEXT.CUSTOM_LABELS_DESCRIPTION') }}
      </div>
    </div>
    <div
      class="bg-lightest-gray p-4 d-flex justify-content-between align-items-center flex-wrap flex-sm-nowrap"
    >
      <div>
        <h6>{{ $t('LABEL_EDITOR.LABEL.DEVELOPER_MODE') }}</h6>
        <small>{{
          $t('LABEL_EDITOR.TEXT.DEVELOPER_MODE_DESCRIPTION', {
            applicationName,
          })
        }}</small>
      </div>
      <HMSwitch v-model="isAdvancedDisplay" class="mt-2 mt-sm-0 ml-0 ml-sm-4" />
    </div>
    <!--    <v-select-->
    <!--      :options="languages"-->
    <!--      :reduce="language => language.value"-->
    <!--      :clearable="false"-->
    <!--      label="label"-->
    <!--      v-model="currentLang"-->
    <!--      class="my-3"-->
    <!--    />-->
    <template v-if="isAdvancedDisplay">
      <HMSearch v-model="search" class="mt-4" />
      <div class="d-flex flex-wrap align-items-center my-2">
        <small class="mr-2">{{ $t('LABEL_EDITOR.LABEL.SUGGESTIONS') }}</small>
        <div
          v-for="(suggestion, index) in searchSuggestions"
          :key="`suggestion${index}`"
        >
          <HMTag
            class="small font-weight-bold bg-light-gray mr-1 my-1 pointer d-block"
            :title="suggestion.label"
            @click.native="setSearchText(suggestion.value)"
          />
        </div>
      </div>
      <TranslationGroup
        v-if="!isLoading"
        :collapsed="!search"
        @input="updateTranslation"
        :group="currentLangValues"
        class="my-3"
      />
    </template>
  </div>
</template>
<script>
import { set, get } from 'lodash';
import filterDeep from 'deepdash-es/filterDeep';
import { mapState, mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import {
  SET_SINGLE_TRANSLATION,
  SET_CURRENT_TRANSLATION_LANG,
  REMOVE_SINGLE_TRANSLATION,
} from '@/core/store/mutation-types';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import TranslationGroup from '@/web/components/settings/TranslationGroup.vue';
import DeleteAllTranslationsModal from '@/web/components/settings/DeleteAllTranslationsModal.vue';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import { getConfig } from '@/web/utils/configuration';

const SIMPLE_TRANS_KEYS = ['STUDIO', 'STUDIO_SIGNUP', 'GENERAL.BUTTON'];

export default {
  name: 'LabelEditor',
  mixins: [DirtyMixin],
  components: {
    TopNav,
    NavItem,
    TranslationGroup,
  },
  computed: {
    ...mapState('studio', ['translations']),
    ...mapGetters('user', ['teacherStudioUrl']),
    currentLangValues() {
      if (!this.search || !this.isAdvancedDisplay) {
        return {};
      }
      const filteredSearchValues = filterDeep(
        this.filteredLangValues,
        (value, key, parentValue, context) => {
          if (key.indexOf(this.search) > -1) {
            // Case sensitive key match
            return true;
          }
          if (value.toLowerCase().indexOf(this.search.toLowerCase()) > -1) {
            // Case insensitive default text value match
            return true;
          }
          if (
            get(
              this.$store.state.studio.translations?.translations,
              `${this.currentLang}.${context.path}`
            )
              ?.toLowerCase()
              .indexOf(this.search.toLowerCase()) > -1
          ) {
            // Case insensitive custom labels text value match
            return true;
          }
          return false;
        }
      );
      return filteredSearchValues;
    },
    unfilteredLangValues() {
      // eslint-disable-next-line
      const values = require(`@/core/locales/${this.currentLang}.json`);
      return values;
    },
    filteredLangValues() {
      if (this.isAdvancedDisplay) {
        return this.unfilteredLangValues;
      }
      const filteredValues = {};
      SIMPLE_TRANS_KEYS.forEach(key => {
        const value = get(this.unfilteredLangValues, key);
        if (value) {
          set(filteredValues, key, value);
        }
      });
      return filteredValues;
    },
    applicationName() {
      return getConfig().applicationName;
    },
  },
  data() {
    return {
      currentLang: 'en',
      isLoading: true,
      inProgress: false,
      isAdvancedDisplay: false,
      search: '',
      searchSuggestions: [
        { label: this.$t('LABEL_EDITOR.LABEL.PRODUCT'), value: 'Product' },
        { label: this.$t('LABEL_EDITOR.LABEL.EVENT'), value: 'Event' },
        { label: this.$t('LABEL_EDITOR.LABEL.SHOP'), value: 'Shop' },
      ],
      languages: [
        { label: this.$t('LABEL_EDITOR.LABEL.EN'), value: 'en' },
        // { label: this.$t('LABEL_EDITOR.LABEL.DE'), value: 'de' },
        // { label: this.$t('LABEL_EDITOR.LABEL.ES'), value: 'es' },
        // { label: this.$t('LABEL_EDITOR.LABEL.FR'), value: 'fr' },
        // { label: this.$t('LABEL_EDITOR.LABEL.HI'), value: 'hi' },
        // { label: this.$t('LABEL_EDITOR.LABEL.IT'), value: 'it' },
        // { label: this.$t('LABEL_EDITOR.LABEL.JA'), value: 'ja' },
        // { label: this.$t('LABEL_EDITOR.LABEL.NL'), value: 'nl' },
        // { label: this.$t('LABEL_EDITOR.LABEL.PT'), value: 'pt' },
        // { label: this.$t('LABEL_EDITOR.LABEL.RU'), value: 'ru' },
        // { label: this.$t('LABEL_EDITOR.LABEL.ZH'), value: 'zh' },
      ],
    };
  },
  methods: {
    updateTranslation({ translationText, translationKey }) {
      this.setDirty();
      if (!translationText || !translationText.trim()) {
        this.$store.commit(`studio/${REMOVE_SINGLE_TRANSLATION}`, {
          translationKey: `${this.currentLang}.${translationKey}`,
        });
        return;
      }
      this.$store.commit(`studio/${SET_SINGLE_TRANSLATION}`, {
        translationKey: `${this.currentLang}.${translationKey}`,
        translationText: translationText,
      });
    },
    async saveTranslations() {
      if (this.inProgress) {
        return;
      }
      this.inProgress = true;
      try {
        const dispatchPromise = await this.$store.dispatch(
          'studio/saveTranslations'
        );
        this.$emit('onSubmit', dispatchPromise);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('LABEL_EDITOR.TEXT.TRANSLATIONS_UPDATE_SUCCESS'),
        });
        this.clearDirty();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('LABEL_EDITOR.TEXT.TRANSLATIONS_UPDATE_ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
      this.inProgress = false;
    },
    showDeleteTranslationsModal() {
      this.$modal.show(DeleteAllTranslationsModal);
    },
    setSearchText(text) {
      this.search = text;
    },
  },
  watch: {
    currentLang: {
      handler(value) {
        this.$store.commit(`studio/${SET_CURRENT_TRANSLATION_LANG}`, value);
      },
      immediate: true,
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch(
        'studio/loadTranslations',
        this.teacherStudioUrl
      );
      this.isLoading = false;
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load translations',
        text: errorToMessage(error?.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

::v-deep .hm-toggle-button--false-active {
  background-color: $light-gray !important;
}
::v-deep .hm-toggle-button-group {
  background-color: $white;
}
::v-deep .hm-toggle-button {
  margin: 0 !important;
}
</style>
